// src/components/TaskPanel.jsx
// Admin task management view — task list + Smart Tasks AI flow + draft review.

import React, { useState as useState_TP } from "react";
import * as PPIcons from "./_icons";
import { Avatar, Button, Chip, Empty, SectionHead, StatusChip, formatRelative } from "./_primitives";

function TaskPanel({
  tasks = [],
  members = [],
  allPosts = [],
  onCreateTask, onAssignTask, onDeleteTask,
  onBulkSmartTasks, onSuggestTask,
  onApproveDraft, onDismissDraft,
}) {
  const drafts  = tasks.filter(t => t.status === "draft");
  const open    = tasks.filter(t => t.status === "open");
  const done    = tasks.filter(t => t.status === "completed");
  const [busy, setBusy] = useState_TP(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Smart tasks call-to-action */}
      <div style={{
        background: "linear-gradient(135deg, var(--accent-soft), transparent 60%)",
        border: "1px solid var(--accent-soft-bd)",
        borderRadius: "var(--radius)", padding: 16,
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: "var(--accent)", color: "var(--accent-fg)",
          display: "grid", placeItems: "center",
        }}>
          <PPIcons.IconSparkle />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: "var(--text-strong)", fontSize: 14 }}>
            Smart Tasks
          </div>
          <div style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 2 }}>
            Cluster unassigned reports into 5 actionable draft tasks. AI matches to your vendors.
          </div>
        </div>
        <Button
          variant="primary"
          icon={<PPIcons.IconSparkle />}
          disabled={busy}
          onClick={async () => { setBusy(true); await onBulkSmartTasks?.(); setBusy(false); }}>
          {busy ? "Generating…" : "Generate 5 drafts"}
        </Button>
      </div>

      {/* Drafts */}
      {drafts.length > 0 && (
        <div className="pp-surface">
          <SectionHead
            title="Draft tasks · review"
            sub={`${drafts.length} pending approval`}
            right={
              <Button variant="ghost" size="sm">Dismiss all</Button>
            }
          />
          <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {drafts.map(t => (
              <DraftRow
                key={t.id} task={t} posts={allPosts}
                onApprove={onApproveDraft}
                onDismiss={onDismissDraft}
              />
            ))}
          </div>
        </div>
      )}

      {/* Open tasks */}
      <div className="pp-surface">
        <SectionHead
          title="Open tasks"
          sub={`${open.length} active`}
          right={
            <div style={{ display: "flex", gap: 6 }}>
              <Button variant="secondary" size="sm" icon={<PPIcons.IconSparkle />} onClick={onSuggestTask}>
                Smart task
              </Button>
              <Button variant="primary" size="sm" icon={<PPIcons.IconPlus />} onClick={onCreateTask}>
                New task
              </Button>
            </div>
          }
        />
        <div style={{ padding: 6 }}>
          {open.length === 0 ? (
            <Empty icon={<PPIcons.IconClipboard />} title="No open tasks" />
          ) : open.map(t => (
            <TaskRow key={t.id} task={t} posts={allPosts} members={members} onDelete={onDeleteTask} />
          ))}
        </div>
      </div>

      {/* Completed */}
      {done.length > 0 && (
        <details className="pp-surface" style={{ padding: 0 }}>
          <summary style={{
            padding: 14, cursor: "pointer", fontSize: 13, fontWeight: 700,
            color: "var(--text-dim)", listStyle: "none",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <PPIcons.IconCheck style={{ width: 14, height: 14 }} />
            Completed · {done.length}
          </summary>
          <div style={{ padding: "0 6px 6px" }}>
            {done.map(t => <TaskRow key={t.id} task={t} posts={allPosts} members={members} />)}
          </div>
        </details>
      )}
    </div>
  );
}

function TaskRow({ task, posts = [], members = [], onDelete }) {
  const linkedPosts = (task.postIds || []).map(id => posts.find(p => p.id === id)).filter(Boolean);
  return (
    <div style={{
      padding: "12px 14px", borderRadius: "var(--radius-sm)",
      display: "flex", alignItems: "center", gap: 12,
      cursor: "pointer", transition: "background .12s",
    }}
      onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          {task.autoGenerated && <Chip tone="ai" dot={false}>Auto</Chip>}
          {task.status === "completed" ? <StatusChip status="resolved" /> :
           task.accepted === true ? <StatusChip status="investigating" /> :
           <StatusChip status="new" />}
        </div>
        <div style={{
          fontSize: 13, fontWeight: 600, color: "var(--text)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{task.title}</div>
        <div style={{
          fontSize: 11, color: "var(--text-faint)", fontFamily: "var(--font-mono)",
          marginTop: 2, display: "flex", gap: 10,
        }}>
          <span>{formatRelative(task.createdAt)}</span>
          {linkedPosts.length > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
              <PPIcons.IconLink style={{ width: 10, height: 10 }} />
              {linkedPosts.length}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {task.assignedToName ? (
          <>
            <Avatar name={task.assignedToName} size="sm" />
            <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{task.assignedToName}</span>
          </>
        ) : (
          <Chip tone="category" dot={false}>Unassigned</Chip>
        )}
      </div>
    </div>
  );
}

function DraftRow({ task, posts = [], onApprove, onDismiss }) {
  const [title, setTitle] = useState_TP(task.title);
  const [desc, setDesc] = useState_TP(task.description);
  const linkedPosts = (task.postIds || []).map(id => posts.find(p => p.id === id)).filter(Boolean);

  return (
    <div style={{
      border: "1px solid var(--accent-soft-bd)",
      background: "var(--accent-soft)",
      borderRadius: "var(--radius-sm)", padding: 14,
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Chip tone="ai" dot={false}>
          <PPIcons.IconSparkle style={{ width: 11, height: 11 }} /> Draft
        </Chip>
        <span style={{ fontSize: 11, color: "var(--text-faint)", fontFamily: "var(--font-mono)" }}>
          Clustered from {linkedPosts.length} pinch points
        </span>
      </div>
      <input
        className="pp-input"
        value={title}
        onChange={e => setTitle(e.target.value)}
        style={{ fontWeight: 700, fontSize: 14 }}
      />
      <textarea
        className="pp-textarea"
        value={desc}
        onChange={e => setDesc(e.target.value)}
        rows={2}
        style={{ minHeight: 60 }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="primary" size="sm" icon={<PPIcons.IconCheck />}
          onClick={() => onApprove && onApprove(task.id, title, desc)}>
          Approve
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDismiss && onDismiss(task)}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

export default TaskPanel;
